import * as React from 'react';
import { connect } from 'react-redux';

import PageHeading from 'components/page/page-heading/PageHeading';

import CampaignBlank from 'modules/campaigns/components/CampaignBlank';
import CampaignPayouts from 'modules/campaigns/components/payments/payouts/CampaignPayouts';
import CampaignPaymentsInvoice from 'modules/campaigns/components/payments/invoice/CampaignPaymentsInvoice';
import CampaignPurchaseOrder from 'modules/campaigns/components/purchase-order/CampaignPurchaseOrder';

import { fetchCampaignPayments, resetCampaignPayments } from 'modules/campaigns/reducers/campaignPayments';
import { fetchCampaignOverview } from 'modules/campaigns/reducers/campaignOverview';
import { fetchSubcampaigns } from 'modules/campaigns/reducers/campaignSubcampaigns';
import { fetchVariablePayments, resetVariablePayments } from 'modules/campaigns/reducers/variable-payments/variable-payments';

import { getAgreedSubcampaigns } from 'utils/subcampaign';
import { __t, applyTranslations } from 'utils/language';
import { isInactive } from 'utils/campaign';
import { isManagedServiceSubscription } from 'utils/subscription';

import './campaign-payments.scss';

applyTranslations(require('./campaign-payments.i18n.json'));

import type { Campaign, Organization, Payment, Subcampaign } from 'types';

type StateProps = {
  subcampaignsList: Array<Subcampaign>,
  campaign: Campaign,
  isSubcampaignsListLoading: boolean,
  campaignPayments: Payment,
  currentOrganization: Organization
};

type OwnProps = {
  campaignId: number
};

type DispatchProps = {
  dispatchFetchSubcampaigns: (a: number) => Promise<any>,
  dispatchFetchCampaignOverview: (a: number) => void,
  dispatchFetchCampaignPayments: (a: number) => Promise<any>,
  dispatchResetCampaignPayments: () => Promise<any>,
  dispatchFetchVariablePayments: (a: number) => void,
  dispatchResetVariablePayments: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

const MSG_KEY = 'campaign_payments';

class CampaignPayments extends React.PureComponent<Props> {
  componentDidMount() {
    const { campaignId, dispatchFetchCampaignPayments, dispatchFetchSubcampaigns, dispatchFetchVariablePayments } = this.props;

    dispatchFetchCampaignPayments(campaignId);
    dispatchFetchSubcampaigns(campaignId);
    dispatchFetchVariablePayments(campaignId);
  }

  componentWillUnmount() {
    const { dispatchResetCampaignPayments, dispatchResetVariablePayments } = this.props;

    dispatchResetCampaignPayments();
    dispatchResetVariablePayments();
  }

  handleVariablePaymentsUpdated = (): void => {
    const { campaignId, dispatchFetchCampaignOverview, dispatchFetchCampaignPayments } = this.props;

    dispatchFetchCampaignOverview(campaignId);
    dispatchFetchCampaignPayments(campaignId);
  };

  render() {
    const { subcampaignsList, campaign, campaignPayments, currentOrganization, isSubcampaignsListLoading } = this.props;

    if (!campaign || !campaign.id || !campaignPayments || isSubcampaignsListLoading) {
      // during loading state
      return null;
    }

    const agreedSubcampaigns = getAgreedSubcampaigns(subcampaignsList);

    if (agreedSubcampaigns.length === 0 && !isInactive(campaign)) {
      return (
        <CampaignBlank>
          <p>
            {__t(`${MSG_KEY}.messages.no_posts_one`)}
          </p>
          <p>
            {__t(`${MSG_KEY}.messages.no_posts_two`)}
          </p>
        </CampaignBlank>
      );
    }

    return (
      <section>
        <section>
          <PageHeading
            heading={__t(`${MSG_KEY}.labels.invoice`)}
            message={__t(`${MSG_KEY}.messages.invoice_description`)}
            color="light"
            size="h1"
            customClass="campaign_payments__section-heading"
          />
          <CampaignPaymentsInvoice
            campaign={campaign}
            payments={campaignPayments}
            subcampaignsList={agreedSubcampaigns}
            onVariablePaymentsUpdate={this.handleVariablePaymentsUpdated}
          />
          {
            !isManagedServiceSubscription(currentOrganization) && (
              <CampaignPurchaseOrder
                campaignId={campaign.id}
              />
            )
          }
        </section>
        <section>
          <PageHeading
            heading={__t(`${MSG_KEY}.labels.payments`)}
            message={`<div>${__t(`${MSG_KEY}.messages.release_payment`)}</div><div>${__t(`${MSG_KEY}.messages.release_deadline`)}</div>`}
            color="light"
            size="h1"
            customClass="campaign_payments__section-heading"
          />
          <CampaignPayouts
            campaign={campaign}
            subcampaignsList={agreedSubcampaigns}
            onVariablePaymentsUpdate={this.handleVariablePaymentsUpdated}
          />
        </section>
      </section>
    );
  }
}

const mapStateToProps = ({ campaignPayments, campaignSubcampaigns, campaignBrief, organizations }): StateProps => ({
  isSubcampaignsListLoading: campaignSubcampaigns.isLoading,
  subcampaignsList: campaignSubcampaigns.list,
  campaign: campaignBrief.brief,
  campaignPayments: campaignPayments.payments,
  currentOrganization: organizations.currentOrganization
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchSubcampaigns: (campaignId: number) => dispatch(fetchSubcampaigns(campaignId, { showLoadingComponent: false })),
  dispatchFetchCampaignPayments: (campaignId: number) => dispatch(fetchCampaignPayments(campaignId)),
  dispatchResetCampaignPayments: () => dispatch(resetCampaignPayments()),
  dispatchFetchCampaignOverview: (campaignId: number): void => dispatch(fetchCampaignOverview(campaignId)),
  dispatchFetchVariablePayments: (campaignId: number): void => dispatch(fetchVariablePayments(campaignId)),
  dispatchResetVariablePayments: (): void => dispatch(resetVariablePayments())
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignPayments);
